import { useEffect, useRef, useCallback, MutableRefObject } from 'react';
import LogicFlow from '@logicflow/core';
import sqlNode from '../utils/sqlNode';
import {
  BezierEdgeConfig,
  getDynamicEdgeType,
  PolylineEdgeConfig,
} from '../utils/sqlEdge';
import { calculateCanvasSize } from '../../OPMEditor/utils/logicFlowHelpers';
import { message } from 'antd';
import { DataMapApi } from '@/api/dataMapApi';
import { RelationshipDataType } from './useRelationshipModal';

interface UseLogicFlowInitializerProps {
  mapViewsId: string | null;
  canvasRef: React.RefObject<HTMLDivElement>;
  lfRef: MutableRefObject<LogicFlow | null>;
  initialData: LogicFlow.GraphConfigData;
  onNodeClick?: (data: any, e: Event) => void;
  onEdgeClick?: (data: any) => void;
  onRelationshipModalOpen?: (
    data: RelationshipDataType,
    edgeId: string | null
  ) => void;
  selectedNodeIdRef?: React.RefObject<string | null>;
  selectedEdgeIdRef?: React.RefObject<string | null>;
  hideNodeMenu?: () => void;
  setSelectedEdgeId?: (edgeId: string | null) => void;
}

const useLogicFlowInitializer = ({
  mapViewsId,
  canvasRef,
  lfRef,
  initialData,
  onNodeClick,
  onEdgeClick,
  onRelationshipModalOpen,
  selectedNodeIdRef,
  selectedEdgeIdRef,
  hideNodeMenu,
  setSelectedEdgeId,
}: UseLogicFlowInitializerProps) => {
  // 用于跟踪已通过 API 删除的边线，避免 edge:delete 事件重复调用
  const deletedEdgesRef = useRef<Set<string>>(new Set());
  
  const deleteEdgeWithApi = useCallback(
    async (edgeId: string) => {
      if (!lfRef.current) return;

      try {
        // 获取当前边线
        const edge = lfRef.current.getEdgeDataById(edgeId);
        if (!edge) {
          console.error('未找到边线:', edgeId);
          return;
        }

        // 获取源节点和目标节点
        const sourceNode = lfRef.current.graphModel.nodes.find(
          (node: any) => String(node.id) === String(edge.sourceNodeId)
        );
        const targetNode = lfRef.current.graphModel.nodes.find(
          (node: any) => String(node.id) === String(edge.targetNodeId)
        );

        // 判断是否是表与表的边线
        const isTableToTable =
          !sourceNode?.properties?.isItOntology &&
          !targetNode?.properties?.isItOntology;

        let res;
        if (isTableToTable) {
          // 表与表的边线，调用 mappingsViewsDeleteTableJoin 接口
          res = await DataMapApi.mappingsViewsDeleteTableJoin(edgeId);
        } else {
          // 表与本体的边线，调用 mappingsViewsDeleteField 接口
          res = await DataMapApi.mappingsViewsDeleteField(edgeId);
        }

        console.log('删除边线API调用结果:', res);
        if (res.code === 200) {
          message.success('映射视图删除边线成功');
          // 标记此边线已通过 API 删除，避免 edge:delete 事件重复调用
          deletedEdgesRef.current.add(edgeId);
          // 设置超时清理，防止内存泄漏（1秒后自动清理标记）
          setTimeout(() => {
            deletedEdgesRef.current.delete(edgeId);
          }, 1000);
          lfRef.current.deleteEdge(edgeId);
          setSelectedEdgeId?.(null);
        }
      } catch (error) {
        console.error('删除边线失败:', error);
        // API调用失败时，清理deletedEdgesRef中的标记
        deletedEdgesRef.current.delete(edgeId);
      }
    },
    [lfRef, setSelectedEdgeId]
  );

  const deleteNodeWithApi = useCallback(
    async (nodeId: string) => {
      if (!lfRef.current) return;
      if (!mapViewsId) return;

      try {
        console.log('开始删除表格:', nodeId, lfRef.current.getNodeDataById(nodeId));
        const currentNode = lfRef.current.getNodeDataById(nodeId);
        if(currentNode?.properties?.isItOntology){
          return message.warning('本体对象节点不能删除！');
        }

        // 1. 先获取所有与该节点相关的边线
        const graphData = lfRef.current.getGraphData() as any;
        const relatedEdges = graphData.edges.filter(
          (edge: any) => 
            String(edge.sourceNodeId) === String(nodeId) || 
            String(edge.targetNodeId) === String(nodeId)
        );
        
        console.log('找到相关边线:', relatedEdges.length, relatedEdges);

        // 2. 删除表节点（后端）
        const res = await DataMapApi.mappingsViewsDeleteTable(
          mapViewsId,
          nodeId
        );
        console.log('删除表格API调用结果:', res);
        
        if (res.code === 200) {
          // 3. 先逐条从画布删除关联边线并标记，确保 edge:delete 事件触发前标记已就绪
          relatedEdges.forEach((edge: any) => {
            const eid = String(edge.id);
            deletedEdgesRef.current.add(eid);
            // 超时兜底清理，防止内存泄漏
            setTimeout(() => {
              deletedEdgesRef.current.delete(eid);
            }, 2000);
            lfRef.current?.deleteEdge(eid);
          });

          // 4. 从画布删除节点（此时关联边线已手动删除，不会再触发 edge:delete）
          console.log('从画布删除表:', nodeId);
          lfRef.current.deleteNode(nodeId);

          // 5. 清除节点选择
          hideNodeMenu?.();
          message.success('映射视图删除表数据成功');
        } else {
          console.log('删除表格API调用失败:', res);
          message.error('删除表格失败');
        }
      } catch (error) {
        console.error('映射视图删除表数据失败:', error);
        message.error('删除表格失败，请重试');
      }
    },
    [hideNodeMenu, lfRef, mapViewsId]
  );
  /** 映射视图添加表字段映射 */
  const mapViewsAddTableFlied = async (
    ontologyAttributeId: string,
    columnMetadataId: string,
    viewSourceId: string
  ) => {
    try {
      if (!mapViewsId) return;
      const res = await DataMapApi.mappingsViewsAddField(
        mapViewsId,
        ontologyAttributeId,
        columnMetadataId,
        viewSourceId
      );
      console.log('映射试图添加表字段映射', res);

      if (res.code === 200) {
        message.success('映射试图添加表字段映射成功');
      } else {
        message.error('映射试图添加表字段映射失败，请重试');
      }
    } catch (error) {
      message.error('映射试图添加表字段映射失败');
    }
  };

  /** 解析锚点信息，获取连接的字段信息 */
  const getAnchorFieldInfo = (anchorId: string, node: any) => {
    // 检查是否是字段级锚点
    if (anchorId.includes('_left') || anchorId.includes('_right')) {
      // 提取字段名：格式为 ${nodeId}_${fieldName}_left/right
      // 用 node.id 作为前缀截取，避免 nodeId 本身含下划线（如 ontology_123）时解析出错
      const nodeId = String(node?.id ?? '');
      const direction = anchorId.endsWith('_left') ? 'left' : 'right';
      const prefix = `${nodeId}_`;
      const suffix = `_${direction}`;
      if (anchorId.startsWith(prefix) && anchorId.endsWith(suffix)) {
        const fieldName = anchorId.slice(
          prefix.length,
          anchorId.length - suffix.length
        );
        // 从节点的 fields 中查找对应的字段
        if (node?.properties?.fields) {
          const field = node.properties.fields.find(
            (f: any) => f.key === fieldName
          );
          if (field) {
            return {
              isItOntology: node?.properties.isItOntology,
              isFieldAnchor: true,
              fieldName,
              field,
              direction,
            };
          }
        }
      }
    }
    // 检查是否是表级锚点
    if (anchorId.includes('_table_')) {
      return {
        isFieldAnchor: false,
        isTableAnchor: true,
        direction: anchorId.includes('_table_left') ? 'left' : 'right',
      };
    }
    return null;
  };

  /** 绑定事件处理函数 */
  const bindEventHandlers = useCallback(
    (lf: LogicFlow) => {
      console.log('bindEventHandlers');

      // 节点点击事件
      lf.on('node:click', ({ data, e }: any) => {
        // 使用 graphModel.nodes 获取节点
        const nodeFromGraphModel = lf.graphModel.nodes.find(
          (node: any) => node.id === data.id
        );
        onNodeClick?.(data, e);
        // 清除边线选择
        setSelectedEdgeId?.(null);
      });

      // 边线点击事件
      lf.on('edge:click', ({ data }: any) => {
        // 选中边线
        setSelectedEdgeId?.(data.id);
        // 清除节点选择
        hideNodeMenu?.();
      });

      // 锚点拖拽开始事件
      lf.on('anchor:dragstart', ({ data, nodeModel }: any) => {
        if (nodeModel.type === 'sql-node') {
          lf.graphModel.nodes.forEach(node => {
            if (nodeModel.id !== node.id) {
              node.isShowAnchor = true;
              node.setProperties({
                isConnection: true,
              });
            }
          });
        }
      });

      // 锚点拖拽结束事件
      lf.on('anchor:dragend', ({ data, nodeModel }: any) => {
        if (nodeModel.type === 'sql-node') {
          lf.graphModel.nodes.forEach(node => {
            if (nodeModel.id !== node.id) {
              node.isShowAnchor = false;
              lf.deleteProperty(node.id, 'isConnection');
            }
          });
        }
      });

      // 边线添加事件 - 处理表与表关联逻辑和本体对象关联检查
      lf.on('edge:add', async (data: any) => {
        const {
          id,
          sourceNodeId,
          targetNodeId,
          sourceAnchorId,
          targetAnchorId,
        } = data.data;
        try {
          // 检查是否是同一节点的锚点连线，如果是则阻止
          console.log(
            'sourceNodeId====',
            sourceNodeId,
            targetNodeId,
            data.data
          );

          if (sourceNodeId === targetNodeId) {
            lf.deleteEdge(id);
            message.warning('同一表的节点不能映射！');
            return;
          }
          // 获取所有节点和边线
          const allNodes = lf.getGraphRawData().nodes || [];
          const allEdges = lf
            .getGraphRawData()
            .edges.filter(edge => edge.id !== id);

          // 获取源节点和目标节点
          const sourceNode = allNodes.find(node => node.id === sourceNodeId);
          const targetNode = allNodes.find(node => node.id === targetNodeId);

          // 获取源锚点和目标锚点的字段信息
          const sourceAnchorInfo = getAnchorFieldInfo(
            sourceAnchorId,
            sourceNode
          );
          const targetAnchorInfo = getAnchorFieldInfo(
            targetAnchorId,
            targetNode
          );
          console.log('sourceNodeId2222====', targetAnchorId, targetNode);

          // 检查是否是表与本体对象的关联
          const isOntologyEdge =
            sourceNode?.properties?.isItOntology ||
            targetNode?.properties?.isItOntology;

          // 检查是否是表与表的关联
          const isTableToTable =
            !sourceNode?.properties?.isItOntology &&
            !targetNode?.properties?.isItOntology;

          // 阻止表级锚点连接到本体节点（但允许字段级锚点连接）
          if (isOntologyEdge) {
            // 检查是否是表级锚点（ID包含 _table_）
            const isSourceTableAnchor = sourceAnchorId?.includes('_table_');
            const isTargetTableAnchor = targetAnchorId?.includes('_table_');
            
            if (isSourceTableAnchor || isTargetTableAnchor) {
              lf.deleteEdge(id);
              message.warning('表级锚点不能连接到本体节点，请使用字段级锚点进行映射');
              return;
            }
            // 字段级锚点可以连接到本体节点，继续处理
          }

          // 对于表与本体对象的关联（字段级锚点），检查锚点是否已经被使用
          if (isOntologyEdge) {
            // 检查源锚点是否已经被其他边线使用
            const sourceHasEdge = allEdges.some(
              edge =>
                edge.sourceNodeId === sourceNodeId &&
                edge.sourceAnchorId === sourceAnchorId
            );

            // 检查目标锚点是否已经被其他边线使用
            const targetHasEdge = allEdges.some(
              edge =>
                edge.targetNodeId === targetNodeId &&
                edge.targetAnchorId === targetAnchorId
            );

            // 如果源或目标锚点已经被使用，删除边线并提示
            const ontologyAttributeId = !!sourceNode?.properties?.isItOntology
              ? sourceAnchorInfo?.field.id
              : targetAnchorInfo?.field.id;
            const columnMetadataId = !sourceNode?.properties?.isItOntology
              ? sourceAnchorInfo?.field.id
              : targetAnchorInfo?.field.id;
            // viewSourceId 始终取数据表节点的后端ID（非本体节点的前端前缀ID）
            const viewSourceId = sourceNode?.properties?.isItOntology
              ? String(targetNode?.id || '')
              : String(sourceNode?.id || '');

            if (sourceHasEdge || targetHasEdge) {
              // 删除刚刚添加的边线
              lf.deleteEdge(id);
              // 提示用户
              message.error('当前字段已经被其他字段关联，不能重复关联');
              return;
            }
            console.log(
              'mappingsViewsAddField',
              sourceNode,
              sourceAnchorInfo,
              targetAnchorInfo?.direction,
              ontologyAttributeId
            );

            try {
              if (!mapViewsId) return;
              const res = await DataMapApi.mappingsViewsAddField(
                mapViewsId,
                ontologyAttributeId,
                columnMetadataId,
                viewSourceId
              );
              console.log('映射视图添加表字段映射', res);
              if (res.code === 200) {
                try {
                  // 尝试更新边线ID，如果失败则回滚
                  lf.changeEdgeId(id, String(res.data));
                  message.success('映射视图添加表字段映射成功');
                } catch (changeIdError) {
                  console.error('更新边线ID失败:', changeIdError);
                  // changeEdgeId失败，删除边线并提示用户
                  lf.deleteEdge(id);
                  message.error('创建边线失败，请重试');
                  return;
                }
              } else {
                message.error('映射视图添加表字段映射失败，请重试');
                lf.deleteEdge(id);
                return;
              }
            } catch (error) {
              message.error('映射视图添加表字段映射失败');
              lf.deleteEdge(id);
              return;
            }
          }
          // 检查是否是自动创建的边线，如果是，就跳过弹窗逻辑
          // 避免保存按钮点击后，新创建的连线又触发弹窗
          const isAutoCreated = data.data.properties?.autoCreated;
          // 是否两表之间连线
          if (isTableToTable && !isAutoCreated) {
            // 先删除用户刚拖拽创建的临时连线
            // 等用户在弹窗中确认后，再在 handleRelationshipConfirm 中创建正式连线
            lf.deleteEdge(id);
            
            // 检查这两个表之间是否已经存在其他连线（无论顺序）
            let conditions = [],
              existingEdge = null;
            for (let i = allEdges.length - 1; i >= 0; i--) {
              const currentEdge = allEdges[i];
              if (
                (String(currentEdge.sourceNodeId) === String(sourceNodeId) &&
                  String(currentEdge.targetNodeId) === String(targetNodeId)) ||
                (String(currentEdge.sourceNodeId) === String(targetNodeId) &&
                  String(currentEdge.targetNodeId) === String(sourceNodeId))
              ) {
                existingEdge = currentEdge;
                break;
              }
            }

            // 如果存在现有边，获取其conditions属性
            if (existingEdge) {
              conditions = existingEdge.properties?.conditions || [];
            }

            // 设置关联弹窗的表数据
            // 注意：此时传入的 id 是已删除的临时连线ID，不会被使用
            onRelationshipModalOpen?.(
              {
                leftTable: {
                  tableName: sourceNode?.properties?.tableName || '',
                  tableId: String(sourceNode?.id) || '',
                  fields: sourceNode?.properties?.fields || [],
                },
                rightTable: {
                  tableName: targetNode?.properties?.tableName || '',
                  tableId: String(targetNode?.id) || '',
                  fields: targetNode?.properties?.fields || [],
                },
                sourceAnchorId: sourceAnchorId,
                targetAnchorId: targetAnchorId,
                conditions: conditions,
              },
              id
            );
          }
        } catch (error) {
          console.error('edge:add 事件处理失败:', error);
          lf.deleteEdge(id);
        }
      });

      // 画布点击事件
      lf.on('blank:click', () => {
        hideNodeMenu?.();
        // 清除边线选择
        setSelectedEdgeId?.(null);
      });

      // 边删除事件
      lf.on('edge:delete', async ({ data }: any) => {
        try {
          if (!mapViewsId) return;

          const edgeId = String(data.id);
          
          // 检查是否已通过 deleteEdgeWithApi 或节点级联删除标记，避免重复调用 API
          if (deletedEdgesRef.current.has(edgeId)) {
            console.log('边线已标记为跳过 API，edge:delete 事件跳过:', edgeId);
            deletedEdgesRef.current.delete(edgeId);
            return;
          }
          
          // 检查是否是从后端加载的边线
          // 从后端加载的边线会有完整的properties，且没有autoCreated标记
          // 用户确认后创建的边线会有autoCreated=true标记
          // 用户拖拽创建的临时边线properties为空或没有后端数据
          // 优先使用 isBackendEdge 标记，其次检查是否有后端特征（conditions/relationshipType）
          // 最后检查edgeId是否为纯数字字符串（后端ID格式）
          const hasBackendMarker = data.properties?.isBackendEdge === true;
          const hasBackendProperties = data.properties && 
                                       !data.properties.autoCreated && 
                                       (data.properties.conditions || data.properties.relationshipType);
          const hasBackendIdFormat = /^\d+$/.test(String(edgeId));
          
          const isBackendEdge = hasBackendMarker || hasBackendProperties || hasBackendIdFormat;
          
          // 如果不是后端边线，不调用删除API
          if (!isBackendEdge) {
            console.log('跳过临时边线或新创建边线的删除API调用:', edgeId, data.properties);
            return;
          }

          const sourceNode = lf.graphModel.nodes.find(
            (node: any) => node.id === data.sourceNodeId
          );
          const targetNode = lf.graphModel.nodes.find(
            (node: any) => node.id === data.targetNodeId
          );

          // 如果节点已被删除，无法判断边线类型，记录警告并跳过
          if (!sourceNode || !targetNode) {
            console.warn('边线关联的节点已被删除，无法调用删除API:', edgeId, {
              sourceNodeId: data.sourceNodeId,
              targetNodeId: data.targetNodeId,
              sourceNodeExists: !!sourceNode,
              targetNodeExists: !!targetNode
            });
            return;
          }

          // 判断是否是表与表的边线
          const isTableToTable =
            !sourceNode?.properties?.isItOntology &&
            !targetNode?.properties?.isItOntology;

          let res;
          if (isTableToTable) {
            // 表与表的边线，调用 mappingsViewsDeleteTableJoin 接口
            console.log('调用 mappingsViewsDeleteTableJoin:', edgeId);
            res = await DataMapApi.mappingsViewsDeleteTableJoin(edgeId);
          } else {
            // 表与本体的边线，调用 mappingsViewsDeleteField 接口
            console.log('调用 mappingsViewsDeleteField:', edgeId);
            res = await DataMapApi.mappingsViewsDeleteField(edgeId);
          }

          console.log('删除边线API调用结果:', res);
          if (res.code === 200) {
            message.success('映射视图删除边线成功');
          } else {
            console.log('删除边线API调用失败:', res);
          }
        } catch (error) {
          console.error('删除边线失败:', error);
        }
      });
    },
    [
      mapViewsId,
      canvasRef,
      lfRef,
      onNodeClick,
      onEdgeClick,
      onRelationshipModalOpen,
      selectedNodeIdRef,
      selectedEdgeIdRef,
      hideNodeMenu,
      setSelectedEdgeId,
    ]
  );

  /** 初始化LogicFlow */
  const initializeLogicFlow = useCallback(() => {
    if (!canvasRef.current) return;
    try {
      const container = canvasRef.current;
      const { width, height } = calculateCanvasSize(container);
      const lf = new LogicFlow({
        width,
        height,
        container: canvasRef.current,
        grid: true,
      });

      // 注册自定义节点和边
      lf.register(sqlNode);
      lf.register(BezierEdgeConfig);
      lf.register(PolylineEdgeConfig);

      // 设置默认边类型和主题
      lf.setDefaultEdgeType('sql-edge-bezier');
      lf.setTheme({
        bezier: {
          stroke: '#afafaf',
          strokeWidth: 1,
        },
      });

      // 渲染初始数据
      lf.render(initialData);
      lf.translateCenter();

      // 绑定事件处理函数
      bindEventHandlers(lf);

      lfRef.current = lf;
    } catch (error) {
      console.error('初始化LogicFlow失败:', error);
    }
  }, [canvasRef, lfRef, initialData, bindEventHandlers]);

  /** 初始化LogicFlow */
  useEffect(() => {
    initializeLogicFlow();
    // 清理函数
    return () => {
      if (lfRef.current) {
        lfRef.current.destroy();
        lfRef.current = null;
      }
      // 清理删除边线的跟踪记录
      deletedEdgesRef.current.clear();
    };
  }, [initializeLogicFlow]);

  // 监听键盘事件
  useEffect(() => {
    // 键盘事件处理
    const handleKeyDown = async (e: KeyboardEvent) => {
      // 检查当前焦点是否在输入框中
      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.isContentEditable);

      if (isInputFocused) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        // e.preventDefault();
        // e.stopPropagation();
        console.log('selectedEdgeIdRef', selectedEdgeIdRef?.current, e.key);
        console.log('selectedNodeIdRef', selectedNodeIdRef?.current);

        // 删除选中的边线
        if (selectedEdgeIdRef?.current && lfRef.current) {
          await deleteEdgeWithApi(selectedEdgeIdRef.current);
        }
        // 删除选中的表格
        else if (selectedNodeIdRef?.current && lfRef.current) {
          await deleteNodeWithApi(selectedNodeIdRef.current);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // 清理事件监听器
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [deleteEdgeWithApi, deleteNodeWithApi, selectedEdgeIdRef, selectedNodeIdRef]);

  return {
    initializeLogicFlow,
    deleteEdgeWithApi,
    deleteNodeWithApi,
  };
};

export default useLogicFlowInitializer;
